import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { ColorValue, DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { SettingType } from '../drawing.enums'
import { scaledCoordinate } from '../drawing.helper'
import { BarPrice, Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { ChartEvents } from '../../chart.events'
import { getDisplayDate } from '../../../../util/dateTime'
import { Expiry } from '../../../../proto/generated/common'
import {
    clampOiProfileBarThickness,
    clampOiProfileBarWidthPercent,
    OI_PROFILE_DEFAULT_BAR_THICKNESS,
    OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT,
    OI_PROFILE_DEFAULT_PRIMARY_CALL_COLOR,
    OI_PROFILE_DEFAULT_PRIMARY_PUT_COLOR,
    OI_PROFILE_DEFAULT_SECONDARY_CALL_COLOR,
    OI_PROFILE_DEFAULT_SECONDARY_PUT_COLOR,
    OI_PROFILE_MAX_BAR_THICKNESS,
    OI_PROFILE_MAX_BAR_WIDTH_PERCENT,
    OI_PROFILE_MIN_BAR_THICKNESS,
    OI_PROFILE_MIN_BAR_WIDTH_PERCENT,
} from './oiProfile.constants'

export type OiProfileType = 'oiProfile'

export type OiProfileStateType = {
    strike: number,
    primaryCall: number | undefined,
    secondaryCall: number | undefined,
    primaryPut: number | undefined,
    secondaryPut: number | undefined,
    expiry: Expiry | undefined,
}

export type OiProfileDataType = {
    callOpenInterest: number | undefined,
    putOpenInterest: number | undefined,
    callOpenInterestChange: number | undefined,
    putOpenInterestChange: number | undefined,
    strike: number,
    expiry: Expiry | undefined,
}

export type DataRenderType = 'openInterest' | 'openInterestChange' | 'openInterestPercentChange' | 'pcrOi' | 'pcrVolume'

export type OiProfileState = {
    data: Record<number, OiProfileStateType[]>
}

export type OiProfileOptions = {
    drawingType: OiProfileType
}

export type OiProfileSettings = {
    primaryCallColor: ColorValue,
    secondaryCallColor: ColorValue,
    secondaryPutColor: ColorValue,
    primaryPutColor: ColorValue,
    data: Record<number, OiProfileDataType[]>,
    dataRenderType: DataRenderType,
    textColor: ColorValue,
    showLabels: boolean,
    cumulative: boolean,
    indicator: boolean,
    showCallPutOnSameSide: false
    barThickness: number
    /** Max call/put bar extent as a percentage of chart width (each side from profile axis). */
    barWidthPercent: number
}

export type ScaledCoordinateType = {
    p1x: number,
    p2x: number,
    p1y: number,
    p2y: number,
    color: string
}

const MIN_OPACITY = 0.3

export {
    clampOiProfileBarThickness,
    clampOiProfileBarWidthPercent,
    OI_PROFILE_DEFAULT_BAR_THICKNESS,
    OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT,
    OI_PROFILE_MAX_BAR_THICKNESS,
    OI_PROFILE_MAX_BAR_WIDTH_PERCENT,
    OI_PROFILE_MIN_BAR_THICKNESS,
    OI_PROFILE_MIN_BAR_WIDTH_PERCENT,
} from './oiProfile.constants'

class OiProfile extends DrawingBase<OiProfileOptions, OiProfileSettings, OiProfileState> {
    scaledCoordinates: ScaledCoordinateType[]
    coordinatesToStrike: Map<string, OiProfileStateType>
    strikeToExpiryData: Map<number, Map<Expiry | undefined, OiProfileDataType>>

    constructor(options: DrawingOptions<OiProfileOptions, OiProfileSettings, OiProfileState>) {
        super(options)
        this.getCumulativeData(options.settings.data ?? {})
        this._state.data = Object.fromEntries(Object.entries((options.settings.cumulative ? this.getCumulativeData(options.settings.data ?? {}) : options.settings.data) ?? []).map((entry) => {
            return [entry[0].toString(), entry[1].map((dt) => (this.mapDataToState(dt, options.settings.dataRenderType ?? 'openInterest')))]
        }))
        this.coordinatesToStrike = new Map()
        this.strikeToExpiryData = new Map()
        this.scaledCoordinates = []
        setTimeout(() => {
            this.primitive?.requestUpdate()
        }, 0)
    }

    draw = (scope: BitmapCoordinatesRenderingScope) => {
        const ctx = scope.context
        const { data } = this._state
        this.coordinatesToStrike = new Map()
        this.scaledCoordinates = []
        let { primaryCallColor, secondaryCallColor, primaryPutColor, secondaryPutColor } = this._settings
        const barHeight = scaledCoordinate(
            clampOiProfileBarThickness(this._settings.barThickness ?? OI_PROFILE_DEFAULT_BAR_THICKNESS),
            scope.verticalPixelRatio
        )
        const chartWidthLogical =
            scope.horizontalPixelRatio > 0 ? this.canvasWidth / scope.horizontalPixelRatio : this.canvasWidth
        const totalAvailableWidth =
            chartWidthLogical *
            (clampOiProfileBarWidthPercent(this._settings.barWidthPercent ?? OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT) / 100)
        const startPrice = this.coordinateToPrice(0 as Coordinate)
        const endPrice = this.coordinateToPrice(this.canvasHeight - 1 as Coordinate)
        const lastDataPointPosition = this.getLastDataPointPosition()
        Object.entries(data).forEach(([time, strikes], index) => {
            if (!time || !strikes) return

            const scaledX = Number.isNaN(Number(time))
                ? this._settings.showCallPutOnSameSide
                    ? this.canvasWidth - 50
                    : this.canvasWidth - 250
                : lastDataPointPosition + 200 + index * 500

            if (scaledX < 0 || scaledX >= this.canvasWidth) return
            if (strikes.length === 0) return

            ctx.beginPath()
            ctx.setLineDash([2])
            ctx.strokeStyle = '#000000'
            ctx.moveTo(scaledX, 0)
            ctx.lineTo(scaledX, this.canvasHeight)
            ctx.stroke()
            ctx.closePath()

            const startStrikePosition = this.getStartStrikePosition(strikes, endPrice)
            const endStrikePosition = this.getEndStrikePosition(strikes, startPrice)
            if (Number.isNaN(startStrikePosition) || Number.isNaN(endStrikePosition)) return

            // Calculate referenceValue for opacity scaling
            let referenceValue = -Infinity
            for (let i = startStrikePosition; i <= endStrikePosition; i++) {
                const curr = strikes[i]
                const maxCallValue = Math.abs(curr.primaryCall ?? 0) + Math.abs(curr.secondaryCall ?? 0)
                const maxPutValue = Math.abs(curr.primaryPut ?? 0) + Math.abs(curr.secondaryPut ?? 0)
                referenceValue = Math.max(referenceValue, maxCallValue, maxPutValue)
            }

            for (let i = startStrikePosition; i <= endStrikePosition; i++) {
                const dt = strikes[i]
                const priceCoordinate = this.priceToCoordinate(dt.strike)
                if (!priceCoordinate) continue

                const absPC = Math.abs(dt.primaryCall ?? 0)
                const absSC = Math.abs(dt.secondaryCall ?? 0)
                const absPP = Math.abs(dt.primaryPut ?? 0)
                const absSP = Math.abs(dt.secondaryPut ?? 0)
                const py1 = scaledCoordinate(priceCoordinate, this.verticalPixelRatio) - barHeight / 2
                const py2 = scaledCoordinate(priceCoordinate, this.verticalPixelRatio) + barHeight / 2
                const callPx1 = scaledX - scaledCoordinate(((absPC + absSC) / referenceValue) * totalAvailableWidth, scope.horizontalPixelRatio)
                const callPx2 = scaledX
                const primaryCallPx1 = scaledX - scaledCoordinate((absPC / referenceValue) * totalAvailableWidth, scope.horizontalPixelRatio)

                if (py2 < 0 || py1 > this.canvasHeight) continue

                // Secondary Call
                if (dt.secondaryCall !== undefined) {
                    ctx.fillStyle = secondaryCallColor.color
                    ctx.globalAlpha = MIN_OPACITY + secondaryCallColor.opacity * ((absSC + absPC) / referenceValue)*(1 - MIN_OPACITY)
                    ctx.setLineDash([])
                    if (dt.secondaryCall >= 0) {
                        ctx.fillRect(
                            callPx1,
                            this._settings.showCallPutOnSameSide ? py1 - barHeight / 2 : py1,
                            primaryCallPx1 - callPx1 + 1,
                            py2 - py1 + 1
                        )
                    } else {
                        ctx.strokeRect(
                            callPx1,
                            this._settings.showCallPutOnSameSide ? py1 - barHeight / 2 : py1,
                            primaryCallPx1 - callPx1 + 1,
                            py2 - py1 + 1
                        )
                    }
                }

                // Primary Call
                if (dt.primaryCall !== undefined) {
                    ctx.fillStyle = primaryCallColor.color
                    ctx.globalAlpha = MIN_OPACITY + primaryCallColor.opacity * ((absSC + absPC) / referenceValue)*(1 - MIN_OPACITY)
                    ctx.fillRect(
                        primaryCallPx1,
                        this._settings.showCallPutOnSameSide ? py1 - barHeight / 2 : py1,
                        callPx2 - primaryCallPx1 + 1,
                        py2 - py1 + 1
                    )
                }

                const putPx2 = scaledX + scaledCoordinate(((absPP + absSP) / referenceValue) * totalAvailableWidth, scope.horizontalPixelRatio)
                const putPx1 = scaledX
                const secondaryPutPx1 = scaledX + scaledCoordinate((absPP / referenceValue) * totalAvailableWidth, scope.horizontalPixelRatio)

                // Primary Put
                if (dt.primaryPut !== undefined) {
                    ctx.fillStyle = primaryPutColor.color
                    ctx.globalAlpha = MIN_OPACITY + primaryPutColor.opacity * ((absPP + absSP) / referenceValue)*(1 - MIN_OPACITY)
                    ctx.fillRect(
                        this._settings.showCallPutOnSameSide ? putPx1 - (secondaryPutPx1 - putPx1) : putPx1,
                        this._settings.showCallPutOnSameSide ? py1 + barHeight / 2 : py1,
                        secondaryPutPx1 - putPx1,
                        py2 - py1 + 1
                    )
                }

                // Secondary Put
                if (dt.secondaryPut !== undefined) {
                    ctx.fillStyle = secondaryPutColor.color
                    ctx.globalAlpha = MIN_OPACITY + secondaryPutColor.opacity * ((absPP + absSP) / referenceValue)*(1 - MIN_OPACITY)
                    ctx.setLineDash([])
                    if (dt.secondaryPut >= 0) {
                        ctx.fillRect(
                            this._settings.showCallPutOnSameSide
                                ? secondaryPutPx1 - (putPx2 - putPx1 + 1) - (secondaryPutPx1 - putPx1)
                                : secondaryPutPx1,
                            this._settings.showCallPutOnSameSide ? py1 + barHeight / 2 : py1,
                            putPx2 - secondaryPutPx1 + 1,
                            py2 - py1 + 1
                        )
                    } else {
                        ctx.strokeStyle = secondaryPutColor.color
                        ctx.strokeRect(
                            this._settings.showCallPutOnSameSide
                                ? secondaryPutPx1 - (putPx2 - putPx1 + 1) - (secondaryPutPx1 - putPx1)
                                : secondaryPutPx1,
                            this._settings.showCallPutOnSameSide ? py1 + barHeight / 2 : py1,
                            putPx2 - secondaryPutPx1 + 1,
                            py2 - py1 + 1
                        )
                    }
                }

                // Store coordinates for hover detection
                const sc = {
                    p1x: callPx1,
                    p1y: py1,
                    p2x: putPx2,
                    p2y: py2,
                    color: ''
                }
                this.scaledCoordinates.push(sc)
                this.coordinatesToStrike.set(`${sc.p1x},${sc.p1y},${sc.p2x},${sc.p2y}`, dt)
            }

            // Render tag
            const tagText = `${this._settings.cumulative ? '' : getDisplayDate(Number(time))}`
            const padding = 0, verticalSpacing = 8
            ctx.lineWidth = 0.5
            ctx.font = `${14 * window.devicePixelRatio}px sans-serif`
            let boundingWidth = 0
            let boundingHeight = 0
            let tagDimensions = ctx.measureText(tagText)
            boundingWidth = Math.max(boundingWidth, tagDimensions.width)
            const tagHeight = tagDimensions.fontBoundingBoxAscent + tagDimensions.fontBoundingBoxDescent
            boundingHeight += tagHeight
            boundingWidth += 2 * padding
            boundingHeight += 2 * padding
            const [tagX, tagY] = [scaledX - boundingWidth / 2, this.canvasHeight - boundingHeight]
            ctx.textBaseline = 'bottom'
            ctx.fillStyle = '#000000'
            ctx.globalAlpha = 1
            ctx.fillText(tagText, tagX, tagY)
        })
    }


    getLastDataPointPosition(): number {
        const range = this._chart.chartApi.timeScale().getVisibleLogicalRange()
        if (!range) return NaN
        const barsInfo = this.getSeries().barsInLogicalRange(range)
        if (!barsInfo) return NaN
        if (barsInfo.barsAfter < 0) {
            let lastTime: Time | undefined = barsInfo.to;
            if (!lastTime) {
                const primaryData = this._chart.getRenderedPrimaryData()
                if (primaryData.length === 0) return NaN
                const lastBar = primaryData[primaryData.length - 1]
                lastTime = lastBar.time
            }
            const coord = this.timeToCoordinate(Number(lastTime) as Time)
            return scaledCoordinate(coord!, this.horizontalPixelRatio)
        }
        return NaN
    }

    getStartStrikePosition = (strikes: OiProfileStateType[], price: BarPrice | null): number => {

        if(!price) return NaN
        for(let i=0; i<strikes.length; i++) {
            if(strikes[i].strike >= price){
                return i;
            }
        }

        return NaN
    }

    getEndStrikePosition = (strikes: OiProfileStateType[], price: BarPrice | null): number => {

        if(!price) return NaN
        for(let i=strikes.length-1; i>=0; i--) {
            if(strikes[i].strike <= price){
                return i;
            }
        }

        return NaN
    }

    onSettingsUpdate = (settings: Partial<OiProfileSettings>) => {
        if (settings.data) {
            this.getCumulativeData(this._settings.data ?? {})
            this._state.data = Object.fromEntries(Object.entries((this._settings.cumulative ? this.getCumulativeData(this._settings.data ?? {}) : this._settings.data) ?? []).map((entry) => {
                return [entry[0].toString(), entry[1].map((dt) => (this.mapDataToState(dt, this._settings.dataRenderType ?? 'openInterestChange')))]
            }))
        }
        if ('barThickness' in settings || 'barWidthPercent' in settings) {
            this.primitive?.requestUpdate()
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<OiProfileSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'primaryCallColor',
                    label: 'Primary Call Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: OI_PROFILE_DEFAULT_PRIMARY_CALL_COLOR,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'primaryPutColor',
                    label: 'Primary Put Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: OI_PROFILE_DEFAULT_PRIMARY_PUT_COLOR,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'secondaryCallColor',
                    label: 'Secondary Call Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: OI_PROFILE_DEFAULT_SECONDARY_CALL_COLOR,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'secondaryPutColor',
                    label: 'Secondary Put Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: OI_PROFILE_DEFAULT_SECONDARY_PUT_COLOR,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'textColor',
                    label: 'Text Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#2962ff',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'showCallPutOnSameSide',
                    label: 'Show call/put on same side',
                    type: SettingType.BOOLEAN,
                    defaultValue: true,
                    custom: true,
                    visible: true
                },
                {
                    name: 'barThickness',
                    label: 'Bar thickness',
                    type: SettingType.NUMBER,
                    defaultValue: OI_PROFILE_DEFAULT_BAR_THICKNESS,
                    range: [OI_PROFILE_MIN_BAR_THICKNESS, OI_PROFILE_MAX_BAR_THICKNESS],
                    custom: true,
                    visible: true
                },
                {
                    name: 'barWidthPercent',
                    label: 'Bar width (% of chart)',
                    type: SettingType.NUMBER,
                    defaultValue: OI_PROFILE_DEFAULT_BAR_WIDTH_PERCENT,
                    range: [OI_PROFILE_MIN_BAR_WIDTH_PERCENT, OI_PROFILE_MAX_BAR_WIDTH_PERCENT],
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): OiProfileSettings {
        return { ...this._settings }
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'oiProfile':
                return 'oiProfile'
        }
    }

    getMiniSettings = () => {
        if (['openInterestChange'].includes(this._settings.dataRenderType)) {
            return ['primaryCallColor', 'primaryPutColor'] as (keyof OiProfileSettings)[]
        }
        return ['primaryCallColor', 'secondaryCallColor', 'primaryPutColor', 'secondaryPutColor'] as (keyof OiProfileSettings)[]
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const margin = 20
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)

        const sc = this.scaledCoordinates.find((sc) =>
            (scaledX >= sc.p1x - margin && scaledX <= sc.p2x + margin) &&
            (scaledY >= sc.p1y - margin && scaledY <= sc.p2y + margin)
        )
        if (sc) {
            const coordinateToStrike = this.coordinatesToStrike.get(`${sc.p1x},${sc.p1y},${sc.p2x},${sc.p2y}`)
            this._hovered = true
            const data = this._settings.cumulative ? this.strikeToExpiryData.get(coordinateToStrike!.strike) : new Map([[coordinateToStrike!.expiry, this.strikeToExpiryData.get(coordinateToStrike!.strike)?.get(coordinateToStrike?.expiry)]])

            data && this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, {
                type: this.options?.['drawingType'],
                x: x,
                y: y,
                state: coordinateToStrike,
                renderType: this._settings.dataRenderType,
                cumulativeData: this._settings.cumulative
                    ? this.strikeToExpiryData.get(coordinateToStrike!.strike)
                    : new Map([[coordinateToStrike!.expiry, this.strikeToExpiryData.get(coordinateToStrike!.strike)?.get(coordinateToStrike?.expiry)]])
            })
        } else if (this._hovered) {
            this._hovered = false
            this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, null)
        }
        return null
    }

    addBothNumbers(a: number | undefined, b: number | undefined): number | undefined {
        if (a === undefined && b === undefined) {
            return undefined
        } else if (a === undefined) {
            return b
        } else if (b === undefined) {
            return a
        } else {
            return a + b
        }
    }

    getCumulativeData(expiryToTrades: Record<number, OiProfileDataType[]>): Record<number, OiProfileDataType[]> {

        const mp: Map<number, OiProfileDataType> = new Map<number, OiProfileDataType>()
        const strikeToExpiryData: Map<number, Map<Expiry | undefined, OiProfileDataType>> = new Map()

        Object.keys(expiryToTrades).forEach((key) => {
            expiryToTrades[Number(key)].forEach((td) => {
                const strike = td.strike
                const expiryObject = td.expiry

                if (mp.has(strike)) {
                    const oldStrike = {
                        ...mp.get(strike)
                    } as OiProfileDataType
                    mp.set(strike, {
                        strike: oldStrike.strike,
                        callOpenInterest: this.addBothNumbers(oldStrike.callOpenInterest, td.callOpenInterest),
                        putOpenInterest: this.addBothNumbers(oldStrike.putOpenInterest, td.putOpenInterest),
                        callOpenInterestChange: this.addBothNumbers(oldStrike.callOpenInterestChange, td.callOpenInterestChange),
                        putOpenInterestChange: this.addBothNumbers(oldStrike.putOpenInterestChange, td.putOpenInterestChange),
                        expiry: td.expiry
                    } as OiProfileDataType)
                } else {
                    mp.set(strike, td as OiProfileDataType)
                }

                if (!strikeToExpiryData.has(strike)) {
                    strikeToExpiryData.set(strike, new Map<any, OiProfileDataType>())
                }

                const expiryMap = strikeToExpiryData.get(strike)!

                if (expiryMap.has(expiryObject)) {
                    const existingData = expiryMap.get(expiryObject)!
                    expiryMap.set(expiryObject, {
                        strike: existingData.strike,
                        callOpenInterest: this.addBothNumbers(existingData.callOpenInterest, td.callOpenInterest),
                        putOpenInterest: this.addBothNumbers(existingData.putOpenInterest, td.putOpenInterest),
                        callOpenInterestChange: this.addBothNumbers(existingData.callOpenInterestChange, td.callOpenInterestChange),
                        putOpenInterestChange: this.addBothNumbers(existingData.putOpenInterestChange, td.putOpenInterestChange),
                        expiry: expiryObject
                    } as OiProfileDataType)
                } else {
                    expiryMap.set(expiryObject, td as OiProfileDataType)
                }
            })
        })
        this.strikeToExpiryData = strikeToExpiryData
        return {
            NaN: Array.from(mp.values())
        }
    }

    mapDataToState(data: OiProfileDataType, dataType: DataRenderType): OiProfileStateType {
        const getPrimaryCall = () => {
            switch (dataType) {
                case 'openInterest':
                    return data.callOpenInterest
                case 'openInterestChange':
                    return data.callOpenInterestChange
            }
        }
        const getSecondaryCall = () => {
            switch (dataType) {
                case 'openInterest':
                    return data.callOpenInterestChange
                case 'openInterestChange':
                    return undefined
            }
        }
        const getPrimaryPut = () => {
            switch (dataType) {
                case 'openInterest':
                    return data.putOpenInterest
                case 'openInterestChange':
                    return data.putOpenInterestChange
            }
        }
        const getSecondaryPut = () => {
            switch (dataType) {
                case 'openInterest':
                    return data.putOpenInterestChange
                case 'openInterestPercentChange':
                    return undefined
            }
        }

        return {
            expiry: data.expiry,
            strike: data.strike,
            primaryCall: getPrimaryCall(),
            secondaryCall: getSecondaryCall(),
            primaryPut: getPrimaryPut(),
            secondaryPut: getSecondaryPut()
        }
    }
}

export default OiProfile
